import { useEffect, useState } from "react";
import {
  BlogsDataType,
  languagesDataType,
  BlogTranslation,
} from "../../interfaces/admin.interface";
import { Helmet } from "react-helmet";
import { message, Modal, Popconfirm, Table, TableProps } from "antd";
import * as Yup from "yup";
import { useFormik } from "formik";
import { LanguageService } from "../../services/languages.service";
import ErrorMessage from "../../components/shared/ErrorMessage/ErrorMessage";
import MainButton from "../../components/shared/MainButton/MainButton";
import Spinner from "../../components/shared/Spinner/Spinner";
import ChangeLanguage from "../../components/shared/change-language/ChangeLanguage";
import { BlogsService } from "../../services/blogs.service";
import ShowImage from "../../components/shared/show-image/ShowImage";
import SelectImage from "../../components/shared/select-image/SelectImage";
import { FaPlus } from "react-icons/fa";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// ✅ 1. Köməkçi funksiya: Yalnız 'uz' olduqda 'UZ' edir, digərlərini (AZ, en, ru) olduğu kimi saxlayır.
const formatLang = (code: string): string => {
  if (!code) return "";
  // Əgər kod "uz"-dursa (böyük-kiçik fərqi yoxdur), "UZ" qaytar.
  // Digər hallarda olduğu kimi qaytar (Məs: AZ olduğu kimi qalır).
  return code.toLowerCase() === "uz" ? "UZ" : code;
};

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

const Blogs = () => {
  const [blogs, setBlogs] = useState<BlogsDataType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingRecord, setEditingRecord] = useState<BlogsDataType | null>(
    null,
  );

  const [langs, setLangs] = useState<languagesDataType[]>([]);
  // Default olaraq formatlanmış dil kodu istifadə edirik
  const [primaryLangCode, setPrimaryLangCode] = useState<string>("UZ");
  const [langCode, setLangCode] = useState<string>("UZ");

  const [imageFiles, setImageFiles] = useState<{ [key: string]: any[] }>({});
  const [innerImageFiles, setInnerImageFiles] = useState<{
    [key: string]: any[];
  }>({});

  const [showImageModal, setShowImageModal] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string>("");

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ align: [] }],
      ["link", "image"],
      ["clean"],
    ],
  };

  const quillFormats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "list",
    "bullet",
    "align",
    "link",
    "image",
  ];

  // ✅ 2. Datanı map edərkən dilləri yoxlayırıq
  const mapBlogsResponse = (data: any[], primary: string): BlogsDataType[] => {
    return (data ?? []).map((item: any, index: number) => {
      // Primary dili taparkən formatLang istifadə edirik ki, uyğunsuzluq olmasın
      const pTranslation: BlogTranslation | undefined = item.translations?.find(
        (t: BlogTranslation) => formatLang(t.lang_code) === formatLang(primary),
      );

      return {
        ...item,
        key: index + 1,
        // Əgər tərcümədə şəkil yoxdursa, əsas obyektdəki şəkli götürür
        image: pTranslation?.image ?? item.image,
        inner_image: pTranslation?.inner_image ?? item.inner_image,
      } as BlogsDataType;
    });
  };

  useEffect(() => {
    LanguageService.getAllLanguages().then((response) => {
      const data: languagesDataType[] = response?.data ?? [];
      setLangs(data);

      if (data.length > 0) {
        // İlk gələn dili default kimi götürürük və formatlayırıq
        const defaultLang = formatLang(data[0].code);
        setPrimaryLangCode(defaultLang);
        setLangCode(defaultLang);
      }
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    BlogsService.getAllBlogs()
      .then((response) => {
        if (Array.isArray(response?.data?.data)) {
          const mapped = mapBlogsResponse(response.data.data, primaryLangCode);
          setBlogs(mapped);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [primaryLangCode]);

  const handleAfterSubmit = async () => {
    setLoading(true);
    setIsModalVisible(false);
    resetForm();
    setImageFiles({});
    setInnerImageFiles({});
    const updatedData = await BlogsService.getAllBlogs();
    if (Array.isArray(updatedData?.data?.data)) {
      const mapped = mapBlogsResponse(updatedData.data.data, primaryLangCode);
      setBlogs(mapped);
    }
    setLoading(false);
  };

  const { handleSubmit, values, resetForm, setFieldValue, touched, errors } =
    useFormik({
      initialValues: {
        title: "",
        description: "",
        translations: [] as any[],
      },
      validationSchema: Yup.object({
        title: Yup.string().required("Bloqun başlığı tələb olunur"),
        description: Yup.string()
          .required("Bloqun təsviri tələb olunur")
          .min(10, "Təsvir ən azı 10 simvol olmalıdır."),
      }),
      onSubmit: async (vals) => {
        setLoading(true);
        try {
          const getImageForLang = async (
            code: string,
            fileState: { [key: string]: any[] },
          ) => {
            const formattedCode = formatLang(code);
            const files = fileState[formattedCode];

            if (files && files.length > 0) {
              if (files[0].originFileObj) {
                return await fileToBase64(files[0].originFileObj);
              } else if (files[0].url) {
                return files[0].url;
              }
            }
            return "";
          };

          // 1. Əsas dilin şəkillərini hazırlayırıq
          const primaryImage = await getImageForLang(
            primaryLangCode,
            imageFiles,
          );
          const primaryInnerImage = await getImageForLang(
            primaryLangCode,
            innerImageFiles,
          );

          // 2. Digər dillərin tərcümələrini hazırlayırıq
          const finalTranslations = [];

          for (const tr of vals.translations) {
            const code = tr.lang_code;
            const img = await getImageForLang(code, imageFiles);
            const innerImg = await getImageForLang(code, innerImageFiles);

            finalTranslations.push({
              ...tr,
              image: img || tr.image,
              inner_image: innerImg || tr.inner_image,
            });
          }

          // ✅ 3. ƏSAS DİLİ (UZ) DƏ TRANSLATIONS MASSİVİNƏ ƏLAVƏ EDİRİK
          // Redaktə rejimidirsə, ID-ni də tapıb qoyuruq ki, dublikat yaranmasın
          let primaryId = undefined;
          if (isEditing && editingRecord) {
            const existingPrimary = editingRecord.translations?.find(
              (t) => formatLang(t.lang_code) === primaryLangCode,
            );
            if (existingPrimary) {
              primaryId = existingPrimary.id;
            }
          }

          finalTranslations.push({
            id: primaryId, // Əgər varsa ID, yoxdursa undefined
            lang_code: primaryLangCode, // "UZ"
            title: vals.title,
            description: vals.description,
            image: primaryImage, // Yeni yüklənən və ya köhnə URL
            inner_image: primaryInnerImage,
          });

          const payload = {
            title: vals.title,
            description: vals.description,
            image: primaryImage,
            inner_image: primaryInnerImage,
            translations: finalTranslations, // Artıq UZ buradadır
          };

          // Validasiya: Əsas dil üçün şəkil mütləq olmalıdır
          if (!payload.image || !payload.inner_image) {
            message.error(
              `Əsas dil (${primaryLangCode}) üçün şəkillər seçilməlidir.`,
            );
            setLoading(false);
            return;
          }

          if (isEditing && editingRecord) {
            const response = await BlogsService.updateBlogs({
              ...payload,
              id: editingRecord.id,
            });
            if (response.status === 200) {
              message.success("Bloq uğurla yeniləndi!");
              await handleAfterSubmit();
            } else {
              message.error(response.errorMessage || "Yeniləmək alınmadı.");
              setLoading(false);
            }
          } else {
            const response = await BlogsService.addBlogs(payload);
            if (response.status === 201) {
              message.success("Bloq uğurla əlavə edildi!");
              await handleAfterSubmit();
            } else {
              message.error(response.errorMessage || "Əlavə etmək alınmadı.");
              setLoading(false);
            }
          }
        } catch (error) {
          console.log(error);
          message.error("Xəta baş verdi.");
          setLoading(false);
        }
      },
    });

  const handleTranslationChange = (value: string, field: string) => {
    // Cari seçili dil onsuz da formatlanmışdır (formatLang sayəsində)
    if (langCode === primaryLangCode) {
      setFieldValue(field, value);
    } else {
      const translations = [...(values.translations || [])];
      const index = translations.findIndex((t) => t.lang_code === langCode);

      if (index !== -1) {
        translations[index] = { ...translations[index], [field]: value };
        setFieldValue("translations", translations);
      }
    }
  };

  const handleFileChange = (
    newFileList: any,
    type: "image" | "inner_image",
  ) => {
    let fileListArray: any[] = [];
    if (Array.isArray(newFileList)) {
      fileListArray = newFileList;
    }

    const stateSetter = type === "image" ? setImageFiles : setInnerImageFiles;

    // ✅ 4. Şəkli yadda saxlayanda formatlanmış dil kodu ilə saxlayırıq (UZ)
    stateSetter((prev) => ({
      ...prev,
      [formatLang(langCode)]: fileListArray,
    }));
  };

  const handleDeleteClick = async (record: BlogsDataType) => {
    try {
      await BlogsService.deleteBlogs(record.id.toString());
      setBlogs((prevData) => prevData.filter((item) => item.id !== record.id));
      message.success("Bloq uğurla silindi!");
    } catch (error) {
      message.error("Bloqu silmək alınmadı.");
    }
  };

  // Cədvəl sütunları
  const columns: TableProps<BlogsDataType>["columns"] = [
    {
      title: "№",
      dataIndex: "key",
      sorter: (a, b) => (a.key || 0) - (b.key || 0),
    },
    {
      title: "Bloqun başlığı",
      dataIndex: "title",
      sorter: (a, b) => (a.title || "").localeCompare(b.title || ""),
    },
    {
      title: "Bloq haqqında",
      dataIndex: "description",
      render: (_, record) => {
        const plainText = (record.description || "").replace(/<[^>]*>/g, "");
        return (
          <div>
            {plainText && plainText.length > 30
              ? plainText.substring(0, 30) + "..."
              : plainText}
          </div>
        );
      },
    },
    {
      title: "Şəkil",
      dataIndex: "image",
      render: (_, record) => {
        const src = record.image;
        return (
          <div
            style={{ cursor: "pointer" }}
            onClick={() => {
              if (src) {
                setSelectedImage(src);
                setShowImageModal(true);
              }
            }}
          >
            {src ? (
              <img
                style={{ width: "40px" }}
                src={src}
                alt={`${(record.title || "").split(" ", 1)}`}
              />
            ) : null}
          </div>
        );
      },
    },
    {
      title: "Daxili şəkil",
      dataIndex: "inner_image",
      render: (_, record) => {
        const src = record.inner_image;
        return src ? (
          <div
            style={{ cursor: "pointer" }}
            onClick={() => {
              setSelectedImage(src as string);
              setShowImageModal(true);
            }}
          >
            <img
              style={{ width: "40px" }}
              src={src as string}
              alt={`${(record.title || "").split(" ", 1)} daxili şəkil`}
            />
          </div>
        ) : (
          <span style={{ color: "#999" }}>Seçilməyib</span>
        );
      },
    },
    {
      title: (
        <div className="settings">
          <i className="fa-solid fa-gear"></i>
        </div>
      ),
      key: "action",
      render: (_, record) => (
        <div className="settings_icons">
          <div className="edit" onClick={() => handleEditClick(record)}>
            <i className="fa-solid fa-pen-to-square"></i>
          </div>
          <Popconfirm
            title="Bu bloqu siləcəyinizə əminsiniz?"
            onConfirm={() => handleDeleteClick(record)}
            onCancel={() => message.info("Silinmə əməliyyatı ləğv edildi.")}
            okText="Bəli"
            cancelText="Xeyr"
            placement="topRight"
          >
            <div className="delete">
              <i className="fa-solid fa-trash"></i>
            </div>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const handleAddClick = () => {
    setIsEditing(false);
    setEditingRecord(null);
    setIsModalVisible(true);
    resetForm();
    setLangCode(primaryLangCode);
    setImageFiles({});
    setInnerImageFiles({});

    // ✅ 5. Yeni bloq əlavə edərkən translation array-i yaradılır
    // Burada formatLang istifadə edirik ki, 'uz' -> 'UZ' olsun
    const initialTranslations = langs
      .filter((l) => formatLang(l.code) !== primaryLangCode)
      .map((l) => ({
        lang_code: formatLang(l.code),
        title: "",
        description: "",
      }));
    setFieldValue("translations", initialTranslations);
  };

  const handleEditClick = (record: BlogsDataType) => {
    setIsEditing(true);
    setEditingRecord(record);
    setIsModalVisible(true);

    // Primary translation-ı tapanda da formatlayırıq
    const pTranslation = record.translations?.find(
      (t) => formatLang(t.lang_code) === primaryLangCode,
    );

    setFieldValue("title", record.title || "");
    setFieldValue(
      "description",
      pTranslation?.description || record.description || "",
    );

    // ✅ 6. Redaktə zamanı translation array-in doldurulması
    const combinedTranslations = langs
      .filter((l) => formatLang(l.code) !== primaryLangCode)
      .map((l) => {
        // Hər bir dil üçün formatlanmış kod
        const formattedLCode = formatLang(l.code);

        // Datadakı mövcud translation-ı tapmaq
        const exist = record.translations?.find(
          (t) => formatLang(t.lang_code) === formattedLCode,
        );

        return {
          lang_code: formattedLCode,
          title: exist?.title || "",
          description: exist?.description || "",
          image: exist?.image || "",
          inner_image: exist?.inner_image || "",
          id: exist?.id,
        };
      });

    setFieldValue("translations", combinedTranslations);

    // Şəkillərin yüklənməsi
    const newImageFiles: { [key: string]: any[] } = {};
    const newInnerImageFiles: { [key: string]: any[] } = {};

    const pImg = record.image || pTranslation?.image;
    const pInnerImg = record.inner_image || pTranslation?.inner_image;

    if (pImg) {
      newImageFiles[primaryLangCode] = [
        { uid: "-1", name: "image", status: "done", url: pImg },
      ];
    }
    if (pInnerImg) {
      newInnerImageFiles[primaryLangCode] = [
        { uid: "-1", name: "inner_image", status: "done", url: pInnerImg },
      ];
    }

    record.translations?.forEach((t) => {
      const tCode = formatLang(t.lang_code); // uz -> UZ
      if (t.image) {
        newImageFiles[tCode] = [
          { uid: `-${t.id}`, name: "image", status: "done", url: t.image },
        ];
      }
      if (t.inner_image) {
        newInnerImageFiles[tCode] = [
          {
            uid: `-${t.id}`,
            name: "inner_image",
            status: "done",
            url: t.inner_image,
          },
        ];
      }
    });

    setImageFiles(newImageFiles);
    setInnerImageFiles(newInnerImageFiles);
    setLangCode(primaryLangCode);
  };

  return (
    <div id="pages">
      <Helmet>
        <title>Orhan Door | Bloqlar</title>
      </Helmet>
      {loading ? (
        <Spinner />
      ) : (
        <div>
          <div className="page_head">
            <h3 className="page_title">Bloqlar</h3>
          </div>
          <div className="pages_components">
            <MainButton onClick={handleAddClick}>
              <FaPlus size={20} />
            </MainButton>
            <div className="pages_table">
              <Table<BlogsDataType>
                scroll={{ x: "100%" }}
                columns={columns}
                dataSource={blogs}
                rowKey={(record) => record.id.toString()}
                pagination={false}
              />
            </div>
          </div>
        </div>
      )}

      <Modal
        title={isEditing ? "Bloqu redaktə edin" : "Yeni bloq əlavə edin"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        style={{ minWidth: "340px" }}
        centered
        width="90%"
      >
        <form className="modal_form" onSubmit={handleSubmit}>
          {/* ✅ 7. Dil dəyişdiriciyə 'formatLang' tətbiq olunur */}
          <ChangeLanguage
            langCode={langCode}
            setLangCode={(code) => setLangCode(formatLang(code))}
            values={values}
            setFieldValue={setFieldValue}
            langs={langs}
          />

          <div className="form_input">
            <label className="label" htmlFor="title">
              Bloqun başlığı {langCode}
            </label>
            <div className="input_wrapper">
              <input
                name="title"
                id="title"
                placeholder="Bloqun başlığı"
                value={
                  langCode === primaryLangCode
                    ? values.title
                    : values.translations.find((t) => t.lang_code === langCode)
                        ?.title || ""
                }
                onChange={(e) =>
                  handleTranslationChange(e.target.value, "title")
                }
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage error={errors.title} touched={touched.title} />
          </div>

          <div className="form_input" style={{ marginBottom: "24px" }}>
            <label className="label" htmlFor="description">
              Bloqun təsviri {langCode}
            </label>
            <ReactQuill
              key={langCode}
              theme="snow"
              value={
                langCode === primaryLangCode
                  ? values.description
                  : values.translations.find((t) => t.lang_code === langCode)
                      ?.description || ""
              }
              onChange={(content: string) =>
                handleTranslationChange(content, "description")
              }
              modules={quillModules}
              formats={quillFormats}
              style={{
                backgroundColor: "#fff",
                borderRadius: "8px",
                minHeight: "200px",
              }}
            />

            <ErrorMessage
              error={errors.description}
              touched={touched.description}
            />
          </div>

          <div
            style={{
              display: "flex",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <div className="form_input">
              <label className="label">Əsas Şəkil ({langCode})</label>
              <SelectImage
                image_name={`Şəkil seçin (${langCode})`}
                // Şəkili göstərərkən düzgün kodu axtarırıq
                logoFileList={imageFiles[formatLang(langCode)] || []}
                setLogoFileList={(files: any) =>
                  handleFileChange(files, "image")
                }
                field="image"
                setFieldValue={() => {}}
              />
            </div>

            <div className="form_input">
              <label className="label">Daxili Şəkil ({langCode})</label>
              <SelectImage
                image_name={`Daxili şəkil seçin (${langCode})`}
                logoFileList={innerImageFiles[formatLang(langCode)] || []}
                setLogoFileList={(files: any) =>
                  handleFileChange(files, "inner_image")
                }
                field="inner_image"
                setFieldValue={() => {}}
              />
            </div>
          </div>

          <div>
            <MainButton full type="submit" className="form_add_btn">
              {isEditing ? "Redaktə edin" : "Əlavə edin"}
            </MainButton>
          </div>
        </form>
      </Modal>

      <ShowImage
        showImageModal={showImageModal}
        setShowImagesModal={setShowImageModal}
        image={selectedImage}
      />
    </div>
  );
};

export default Blogs;
